import React, { useMemo, useState } from "react";
import { Home, Plus } from "lucide-react";
import toast from "react-hot-toast";

import { RECORD_GROUPS, groupLabel } from "../cmaReportTypes";
import ManualComparableModal from "./ManualComparableModal";

const NAVY = "#1f2a44";
const MAX_STATEMENT = 3;

// A record's identity across a re-search, so ticks survive Refine Search.
export const recordKey = (row) => `${row.source || "manual"}:${row.source_id || row.full_address}`;

const money = (v) => (v == null || v === "" ? "" : `$${Number(v).toLocaleString("en-AU")}`);
const auDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

const SORTS = [
  { key: "distance", label: "Distance" },
  { key: "date", label: "Date" },
  { key: "price", label: "Price" },
];

/**
 * Select Records, as on Property Data: one tab per section with its count, a
 * tick on every row, Select/Deselect All, Sort By and Add Property.
 *
 * Sales and rental reports start with every row ticked (Property Data does the
 * same). A Property Price Statement starts with none ticked, allows three at
 * most and adds a Results Selected tab, because the form takes exactly the
 * three most comparable sales.
 */
const CmaRecordSelection = ({ report, records, selected, onChange, rental, statement, initialTab }) => {
  const groups = RECORD_GROUPS.filter((g) => !(statement && g.key === "listing"));
  const [tab, setTab] = useState(groups.some((g) => g.key === initialTab) ? initialTab : groups[0].key);
  const [sort, setSort] = useState("distance");
  const [showManual, setShowManual] = useState(false);

  const selectedKeys = useMemo(() => new Set(selected.map(recordKey)), [selected]);

  // Rows the agent typed in live only in `selected`, so show them in their tab too.
  const rowsFor = (key) => {
    const found = records?.groups?.[key] || [];
    const foundKeys = new Set(found.map(recordKey));
    const manual = selected.filter((r) => r.group === key && !foundKeys.has(recordKey(r)));
    return [...manual, ...found];
  };

  const rows = useMemo(() => {
    const list = tab === "selected" ? selected : rowsFor(tab);
    const sorted = [...list];
    if (sort === "date") {
      sorted.sort((a, b) => String(b.sold_date || b.listed_date || "").localeCompare(String(a.sold_date || a.listed_date || "")));
    } else if (sort === "price") {
      sorted.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
    } else {
      sorted.sort((a, b) => (a.distance_km ?? Infinity) - (b.distance_km ?? Infinity));
    }
    return sorted;
  }, [tab, sort, records, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (row) => {
    const key = recordKey(row);
    if (selectedKeys.has(key)) {
      onChange(selected.filter((r) => recordKey(r) !== key));
      return;
    }
    if (statement && selected.length >= MAX_STATEMENT) {
      toast.error("A Property Price Statement takes the three most comparable sales. Untick one first.");
      return;
    }
    onChange([...selected, { ...row, group: row.group || tab }]);
  };

  const tabRows = tab === "selected" ? selected : rowsFor(tab);
  const allTicked = tabRows.length > 0 && tabRows.every((r) => selectedKeys.has(recordKey(r)));

  const toggleAll = () => {
    const keys = new Set(tabRows.map(recordKey));
    if (allTicked) {
      onChange(selected.filter((r) => !keys.has(recordKey(r))));
    } else {
      const extra = tabRows.filter((r) => !selectedKeys.has(recordKey(r))).map((r) => ({ ...r, group: r.group || tab }));
      onChange([...selected, ...extra]);
    }
  };

  const addManual = (row) => {
    if (statement && selected.length >= MAX_STATEMENT) {
      toast.error("A Property Price Statement takes at most three comparable sales.");
      return;
    }
    const group = tab === "selected" ? "suburb" : tab;
    onChange([...selected, { ...row, group, source: "manual", source_id: `manual-${Date.now()}` }]);
    setShowManual(false);
  };

  const count = (key) => rowsFor(key).length;
  const priceHeading = rental ? "Rent" : "Price";
  const dateHeading = tab === "listing" ? "Advertised Date" : rental ? "Lease Date" : "Sale Date";

  return (
    <div>
      {statement && (
        <p className="text-sm text-gray-700 bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4">
          <strong>Property Price Statement:</strong> select the three most comparable properties. If only one or two
          comparable properties sold in the period, they must be included. If none of the sales shown is comparable,
          continue without selecting any and give the reason on the next step.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Record sections">
          {groups.map((group) => (
            <button
              key={group.key}
              type="button"
              role="tab"
              aria-selected={tab === group.key}
              onClick={() => setTab(group.key)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-t-md border-b-2 ${
                tab === group.key ? "border-[#1f2a44] text-gray-900 font-semibold bg-white" : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              {groupLabel(group, rental)}
              <span className="px-1.5 rounded text-[11px] font-bold text-white"
                style={{ backgroundColor: group.key === "listing" ? "#f97316" : NAVY }}>
                {count(group.key)}
              </span>
            </button>
          ))}
          {statement && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === "selected"}
              onClick={() => setTab("selected")}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-t-md border-b-2 ${
                tab === "selected" ? "border-[#1f2a44] text-gray-900 font-semibold bg-white" : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              Results Selected
              <span className="px-1.5 rounded text-[11px] font-bold text-white bg-emerald-600">{selected.length}</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500" htmlFor="cma-records-sort">Sort By</label>
          <select id="cma-records-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="px-2 py-1.5 rounded-md border border-gray-200 bg-white text-xs">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {!statement && (
            <button type="button" onClick={toggleAll} className="px-2 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900">
              {allTicked ? "Deselect All" : "Select All"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowManual(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-white text-sm font-semibold"
            style={{ backgroundColor: "#0a7cd8" }}
          >
            <Plus className="w-4 h-4" /> Add Property
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-100 rounded-lg">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs text-white" style={{ backgroundColor: NAVY }}>
              <th className="p-2 w-8">
                {!statement && (
                  <input type="checkbox" aria-label="Tick every row on this tab" checked={allTicked} onChange={toggleAll} />
                )}
              </th>
              <th className="p-2 w-12" aria-label="Photo" />
              <th className="p-2">Address</th>
              <th className="p-2">{priceHeading}</th>
              <th className="p-2">{dateHeading}</th>
              <th className="p-2" title="Bedrooms">Bed</th>
              <th className="p-2" title="Bathrooms">Bath</th>
              <th className="p-2" title="Car spaces">Car</th>
              <th className="p-2">Distance From Target</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="p-6 text-center text-gray-400">
                  No records on this tab. Use Add Property for any {rental ? "lease" : "sale"} you know about.
                </td>
              </tr>
            ) : rows.map((row) => {
              const ticked = selectedKeys.has(recordKey(row));
              return (
                <tr key={recordKey(row)} className={`border-t border-gray-100 ${ticked ? "bg-gray-50" : "bg-white"}`}>
                  <td className="p-2">
                    <input type="checkbox" checked={ticked} onChange={() => toggle(row)}
                      aria-label={`Include ${row.full_address}`} />
                  </td>
                  <td className="p-2">
                    <div className="w-10 h-8 rounded bg-gray-100 overflow-hidden flex items-center justify-center">
                      {row.image_url
                        ? <img src={row.image_url} alt="" className="w-full h-full object-cover" />
                        : <Home className="w-3.5 h-3.5 text-gray-300" />}
                    </div>
                  </td>
                  <td className="p-2">
                    <p className="font-medium text-gray-900">{row.full_address}</p>
                    <p className="text-[11px] text-gray-400">
                      {row.source === "manual" ? "Added by you" : row.source === "homer" ? "Homer feed" : "Collings"}
                    </p>
                  </td>
                  <td className="p-2 whitespace-nowrap">{money(row.price) || row.price_label || "-"}{rental && row.price ? " pw" : ""}</td>
                  <td className="p-2 whitespace-nowrap">{auDate(row.sold_date || row.listed_date) || "-"}</td>
                  <td className="p-2">{row.bedrooms ?? "-"}</td>
                  <td className="p-2">{row.bathrooms ?? "-"}</td>
                  <td className="p-2">{row.car_spaces ?? "-"}</td>
                  <td className="p-2 whitespace-nowrap">
                    {row.distance_km != null ? `${row.distance_approx ? "~" : ""}${row.distance_km}km` : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500 mt-2">
        {selected.length} record{selected.length === 1 ? "" : "s"} selected
        {statement ? ` of ${MAX_STATEMENT}` : ""}.
      </p>

      {showManual && (
        <ManualComparableModal
          defaultKind={tab === "listing" ? (rental ? "for_rent" : "for_sale") : (rental ? "leased" : "sold")}
          defaultSuburb={report?.suburb || ""}
          defaultState={report?.state || "VIC"}
          onClose={() => setShowManual(false)}
          onAdd={addManual}
        />
      )}
    </div>
  );
};

export default CmaRecordSelection;
